import { Pagination } from "@/types/global";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

type Payment = {
  id: number;
  payable_id: number;
  payable_type: string;
  prescription_reference_num: string | null;
  date_of_service: string;
  amount_paid: string;
  check_num: string | null;
  date_check_mailed: string | null;
  patient_first_name: string;
  patient_last_name: string;
  date_of_birth: string;
  npi: string;
  business_name: string;
  business_address: string;
  business_address_line_2: string;
  business_city: string;
  business_state: string;
  business_zip: string;
  note: string | null;
  created_at: string;
  updated_at: string;
  date_check_written: string | null;
  reimbursement_address: string | null;
  reimbursement_address_line2: string | null;
  reimbursement_address_city: string | null;
  reimbursement_address_state: string | null;
  reimbursement_address_zip: string | null;
  program_id: number;
  invoice_number: string | null;
  payable_type_abbr: string;
  address: string;
  address_line2: string;
  city: string;
  state: string;
  zip: string;
  check_totals: string;
}

type PaymentResponse = { data: Payment[], meta: Pagination };

export const useQueryPaymentsByInvoice = ({
  invoiceNumber,
  page
}: {
  invoiceNumber: string;
  page: number;
}) => {
  const params = new URLSearchParams();

  params.append('per', '25');

  if (invoiceNumber) {
    params.append('invoice_number', invoiceNumber);
  }

  if (page) {
    params.append('page', page.toString());
  }

  return useQuery({
    queryKey: ["payments", invoiceNumber, page],
    queryFn: async () => {
      const { data, meta } = await axios.get<PaymentResponse>(`payments?${params.toString()}`);
      return {
        data,
        pagination: meta,
      };
    },
    retry: 0,
    enabled: (!!localStorage.getItem('Authorization') && !!invoiceNumber),
  });
};

export const useQueryPaymentsByPatient = ({
  patientFirstName,
  patientLastName,
  dateOfBirth,
  npi,
  page
}: {
  patientFirstName: string;
  patientLastName: string;
  dateOfBirth: string;
  npi: string;
  page: number;
}) => {
  const params = new URLSearchParams();
  params.append('per', '25');

  if (patientFirstName) {
    params.append('patient_first_name', patientFirstName);
  }

  if (patientLastName) {
    params.append('patient_last_name', patientLastName);
  }

  if (dateOfBirth) {
    params.append('date_of_birth', dateOfBirth);
  }

  if (npi) {
    params.append('npi', npi);
  }

  if (page) {
    params.append('page', page.toString());
  }

  return useQuery({
    queryKey: ["payments", patientFirstName, patientLastName, dateOfBirth, npi, page],
    queryFn: async () => {
      const { data, meta } = await axios.get<PaymentResponse>(`payments?${params.toString()}`);
      return {
        data,
        pagination: meta,
      };
    },
    retry: 0,
    enabled: (!!localStorage.getItem('Authorization') && !!patientFirstName && !!patientLastName && !!dateOfBirth && !!npi),
  });
};
